import type { AdminRoleCode } from '@liyu/shared-types';
import { useMemo } from 'react';
import { useAuth } from './AuthProvider';

/**
 * M1 内容域写权限（15 号文档 8.1）：
 * 仅 CONTENT_EDITOR 与 SUPER_ADMIN 可写入；其余后台角色只读。
 * 前端仅做按钮禁用提示，真实鉴权仍由 AdminJwtGuard + AdminRolesGuard 在服务端执行。
 */
export function useCanEditContent(): boolean {
  const { admin } = useAuth();
  if (!admin) {
    return false;
  }
  return admin.roles.includes('SUPER_ADMIN') || admin.roles.includes('CONTENT_EDITOR');
}

/**
 * M2 活动域写权限（03 号文档第 5 节 / admin-activity.controller.ts）：
 * 仅 ACTIVITY_OPERATOR 与 SUPER_ADMIN 可写入；其余后台角色只读。
 * 前端仅做按钮禁用提示，真实鉴权仍由 AdminJwtGuard + AdminRolesGuard 在服务端执行。
 */
export function useCanManageActivities(): boolean {
  const { admin } = useAuth();
  if (!admin) {
    return false;
  }
  return admin.roles.includes('SUPER_ADMIN') || admin.roles.includes('ACTIVITY_OPERATOR');
}

// ---------------------------------------------------------------------------
// OPT-04/05/06/09：O2 后台运营能力矩阵（23 号 §5.3、25 号 §4.2）
//
// 菜单、路由守卫与按钮禁用全部从本文件的 capability 派生（单一来源）；
// 服务端 API Guard 仍是最终边界，前端隐藏入口不等于获得数据。
// ---------------------------------------------------------------------------

/** O2 新增后台能力标识 */
export type AdminCapability =
  | 'VIEW_USERS'
  | 'MANAGE_MEMBERS'
  | 'MANAGE_AGREEMENTS'
  | 'MANAGE_ADMIN_ACCOUNTS'
  | 'VIEW_AUDIT'
  | 'MANAGE_SERVICE_CONFIG'
  | 'HANDLE_FEEDBACK'
  // O3 运营闭环（30 号文档 §4.4）：会员套餐、退款处置、社区话题
  | 'MANAGE_MEMBERSHIP_PLANS'
  | 'HANDLE_REFUNDS'
  | 'MANAGE_COMMUNITY_TOPICS'
  // B7 借阅域（33 号 §7.2）：书房运营页面与书房配置
  | 'MANAGE_LIBRARY'
  | 'MANAGE_LIBRARY_SETTINGS';

/**
 * 能力 → 允许的后台角色（23 号 §5.3 角色能力矩阵）。
 * - 用户/会员查询：SUPER_ADMIN 查改、CUSTOMER_SERVICE 只读；
 * - 协议发布、后台账号管理、服务配置、审计、会员套餐：仅 SUPER_ADMIN；
 * - 反馈处理：SUPER_ADMIN / CUSTOMER_SERVICE；
 * - 退款处置：SUPER_ADMIN / CUSTOMER_SERVICE（与 AdminRefundController 的角色一致）；
 * - 社区话题：SUPER_ADMIN / MODERATOR（与 AdminCommunityTopicController 一致）。
 * - 借阅域（B7）：A-15~A-18/A-21 后端角色为 `LIBRARY_OPERATOR`（预留，见
 *   shared-types `RESERVED_ADMIN_ROLE_CODES`，尚未并入 ADMIN_ROLE_CODES）+ SUPER_ADMIN，
 *   A-19/A-20 仅 SUPER_ADMIN。本轮不强行并入 LIBRARY_OPERATOR，故两项能力均只映射
 *   SUPER_ADMIN；待角色并入后仅需在此追加。
 */
export const CAPABILITY_ROLES: Record<AdminCapability, readonly AdminRoleCode[]> = {
  VIEW_USERS: ['SUPER_ADMIN', 'CUSTOMER_SERVICE'],
  MANAGE_MEMBERS: ['SUPER_ADMIN'],
  MANAGE_AGREEMENTS: ['SUPER_ADMIN'],
  MANAGE_ADMIN_ACCOUNTS: ['SUPER_ADMIN'],
  VIEW_AUDIT: ['SUPER_ADMIN'],
  MANAGE_SERVICE_CONFIG: ['SUPER_ADMIN'],
  HANDLE_FEEDBACK: ['SUPER_ADMIN', 'CUSTOMER_SERVICE'],
  MANAGE_MEMBERSHIP_PLANS: ['SUPER_ADMIN'],
  HANDLE_REFUNDS: ['SUPER_ADMIN', 'CUSTOMER_SERVICE'],
  MANAGE_COMMUNITY_TOPICS: ['SUPER_ADMIN', 'MODERATOR'],
  MANAGE_LIBRARY: ['SUPER_ADMIN'],
  MANAGE_LIBRARY_SETTINGS: ['SUPER_ADMIN'],
};

/** 403 页面展示的能力说明 */
export const CAPABILITY_LABELS: Record<AdminCapability, string> = {
  VIEW_USERS: '用户与会员查询',
  MANAGE_MEMBERS: '会员开通/变更/终止',
  MANAGE_AGREEMENTS: '协议版本管理',
  MANAGE_ADMIN_ACCOUNTS: '后台账号管理',
  VIEW_AUDIT: '审计日志查看',
  MANAGE_SERVICE_CONFIG: '服务配置管理',
  HANDLE_FEEDBACK: '用户反馈处理',
  MANAGE_MEMBERSHIP_PLANS: '会员套餐管理',
  HANDLE_REFUNDS: '退款处置',
  MANAGE_COMMUNITY_TOPICS: '社区话题管理',
  MANAGE_LIBRARY: '图书借阅运营',
  MANAGE_LIBRARY_SETTINGS: '书房配置',
};

/** 既有菜单沿用原可见性：除客服外的后台角色均可见（23 号 §5.3 客服最小可见集） */
export const EXISTING_MENU_ROLES: readonly AdminRoleCode[] = [
  'SUPER_ADMIN',
  'CONTENT_EDITOR',
  'ACTIVITY_OPERATOR',
  'MODERATOR',
];

export interface AdminMenuItemPermission {
  /** 与 AdminLayout 菜单 key 一致（路由路径） */
  key: string;
  /** null 表示沿用既有菜单可见性（除客服外全部可见） */
  capability: AdminCapability | null;
}

/** 菜单权限矩阵：AdminLayout 只按本表过滤，不在组件内硬编码角色判断 */
export const ADMIN_MENU_PERMISSIONS: AdminMenuItemPermission[] = [
  { key: '/', capability: null },
  { key: '/contents', capability: null },
  { key: '/tags', capability: null },
  { key: '/kols', capability: null },
  { key: '/kols/applications', capability: null },
  { key: '/kols/articles', capability: null },
  { key: '/topics', capability: null },
  { key: '/home-config', capability: null },
  { key: '/activities', capability: null },
  { key: '/community/moderation', capability: null },
  { key: '/community/reports', capability: null },
  { key: '/community/sensitive-words', capability: null },
  { key: '/community/identity', capability: null },
  { key: '/analytics', capability: null },
  { key: '/imports', capability: null },
  { key: '/users', capability: 'VIEW_USERS' },
  { key: '/agreements', capability: 'MANAGE_AGREEMENTS' },
  { key: '/admin-accounts', capability: 'MANAGE_ADMIN_ACCOUNTS' },
  { key: '/audit', capability: 'VIEW_AUDIT' },
  { key: '/service-config', capability: 'MANAGE_SERVICE_CONFIG' },
  { key: '/feedback', capability: 'HANDLE_FEEDBACK' },
  { key: '/membership-plans', capability: 'MANAGE_MEMBERSHIP_PLANS' },
  { key: '/refunds', capability: 'HANDLE_REFUNDS' },
  { key: '/community/topics', capability: 'MANAGE_COMMUNITY_TOPICS' },
  // B7 借阅域（33 号 §7.1/§7.2 菜单结构）
  { key: '/library/books', capability: 'MANAGE_LIBRARY' },
  { key: '/library/copies', capability: 'MANAGE_LIBRARY' },
  { key: '/library/borrowings', capability: 'MANAGE_LIBRARY' },
  { key: '/library/overdue', capability: 'MANAGE_LIBRARY' },
  { key: '/library/reservations', capability: 'MANAGE_LIBRARY' },
  { key: '/library/settings', capability: 'MANAGE_LIBRARY_SETTINGS' },
];

/** 角色是否具备某项能力（与 useHasCapability 同一判定，供菜单/路由复用） */
export function hasCapability(roles: readonly string[], capability: AdminCapability): boolean {
  const allowed = CAPABILITY_ROLES[capability] as readonly string[];
  return roles.some((role) => allowed.includes(role));
}

export function canSeeMenuItem(roles: readonly string[], item: AdminMenuItemPermission): boolean {
  if (item.capability) {
    return hasCapability(roles, item.capability);
  }
  const allowed = EXISTING_MENU_ROLES as readonly string[];
  return roles.some((role) => allowed.includes(role));
}

/** 当前角色可见的菜单 key 列表 */
export function visibleMenuKeys(roles: readonly string[]): string[] {
  return ADMIN_MENU_PERMISSIONS.filter((item) => canSeeMenuItem(roles, item)).map(
    (item) => item.key,
  );
}

export type AdminCapabilityMap = Record<AdminCapability, boolean>;

/** 当前登录后台的全部 O2 能力（未登录时全部为 false） */
export function useCapabilities(): AdminCapabilityMap {
  const { admin } = useAuth();
  const roles = admin?.roles ?? [];
  return useMemo<AdminCapabilityMap>(
    () => ({
      VIEW_USERS: hasCapability(roles, 'VIEW_USERS'),
      MANAGE_MEMBERS: hasCapability(roles, 'MANAGE_MEMBERS'),
      MANAGE_AGREEMENTS: hasCapability(roles, 'MANAGE_AGREEMENTS'),
      MANAGE_ADMIN_ACCOUNTS: hasCapability(roles, 'MANAGE_ADMIN_ACCOUNTS'),
      VIEW_AUDIT: hasCapability(roles, 'VIEW_AUDIT'),
      MANAGE_SERVICE_CONFIG: hasCapability(roles, 'MANAGE_SERVICE_CONFIG'),
      HANDLE_FEEDBACK: hasCapability(roles, 'HANDLE_FEEDBACK'),
      MANAGE_MEMBERSHIP_PLANS: hasCapability(roles, 'MANAGE_MEMBERSHIP_PLANS'),
      HANDLE_REFUNDS: hasCapability(roles, 'HANDLE_REFUNDS'),
      MANAGE_COMMUNITY_TOPICS: hasCapability(roles, 'MANAGE_COMMUNITY_TOPICS'),
      MANAGE_LIBRARY: hasCapability(roles, 'MANAGE_LIBRARY'),
      MANAGE_LIBRARY_SETTINGS: hasCapability(roles, 'MANAGE_LIBRARY_SETTINGS'),
    }),
    [roles],
  );
}

export function useHasCapability(capability: AdminCapability): boolean {
  const { admin } = useAuth();
  return admin ? hasCapability(admin.roles, capability) : false;
}

export function useCanViewUsers(): boolean {
  return useHasCapability('VIEW_USERS');
}

export function useCanManageMembers(): boolean {
  return useHasCapability('MANAGE_MEMBERS');
}

export function useCanManageAgreements(): boolean {
  return useHasCapability('MANAGE_AGREEMENTS');
}

export function useCanManageAdminAccounts(): boolean {
  return useHasCapability('MANAGE_ADMIN_ACCOUNTS');
}

export function useCanViewAudit(): boolean {
  return useHasCapability('VIEW_AUDIT');
}

export function useCanManageServiceConfig(): boolean {
  return useHasCapability('MANAGE_SERVICE_CONFIG');
}

export function useCanHandleFeedback(): boolean {
  return useHasCapability('HANDLE_FEEDBACK');
}

/** O3：会员套餐管理（仅 SUPER_ADMIN，与 AdminMembershipPlanController 一致） */
export function useCanManageMembershipPlans(): boolean {
  return useHasCapability('MANAGE_MEMBERSHIP_PLANS');
}

/** O3：退款处置（SUPER_ADMIN / CUSTOMER_SERVICE） */
export function useCanHandleRefunds(): boolean {
  return useHasCapability('HANDLE_REFUNDS');
}

/** O3：社区话题管理（SUPER_ADMIN / MODERATOR） */
export function useCanManageCommunityTopics(): boolean {
  return useHasCapability('MANAGE_COMMUNITY_TOPICS');
}

/**
 * B7：图书借阅运营（图书 / 实体册 / 借阅审批 / 逾期 / 预约）。
 * 后端 A-15~A-18 同时接受预留角色 `LIBRARY_OPERATOR`，但该角色尚未并入
 * `ADMIN_ROLE_CODES`，前端当前只认 SUPER_ADMIN（不在前端硬编码 Reserved 角色字面量）。
 */
export function useCanManageLibrary(): boolean {
  return useHasCapability('MANAGE_LIBRARY');
}

/** B7：书房配置（借阅政策 + 领取地点，A-19/A-20 仅 SUPER_ADMIN） */
export function useCanManageLibrarySettings(): boolean {
  return useHasCapability('MANAGE_LIBRARY_SETTINGS');
}
